import axios from 'axios';
import authenticatedAxiosInstance from './authenticatedAxiosInstance.service';
import authHeader from './auth-header';

const API_URL = process.env.REACT_APP_API_URL + '/borrowing-base';

// Response interfaces
export interface CreateReportsResponse {
  success: boolean;
  message: string;
  data?: any;
  error?: string;
}

const routes = {
  GET_BILLING_LOGS: `${API_URL}/billing-logs`,
  GET_DASHBOARD: `${API_URL}/dashboard`,
  GET_DOCUSIGN_DOCS: `${API_URL}/docusign-docs`,
  GET_LOAN_PLAN_SUMMARY: `${API_URL}/loan-plan-summary`,
  COMPANIES_WITH_BORROWING_BASE: `${API_URL}/companies-with-borrowing-base`,
  GET_REPORTS: `${API_URL}/reports`,
  GET_REPORTS_PAGINATED: `${API_URL}/reports-paginated`,
  GET_TRANSACTIONS: `${API_URL}/transactions`,
  GET_TRANSACTIONS_BY_COMP_ID: `${API_URL}/transactions-by-comp-id`,
  GET_BILLING_BALANCES: `${API_URL}/billing-balances`,
  TRANSACTIONS_DETAIL_GRID: `${API_URL}/transactions-detail-grid`,
  BILL_TRANSACTIONS: `${API_URL}/bill-transactions`,
  CREATE_REPORTS: `${API_URL}/create-reports`,
  DELETE_REPORT: `${API_URL}/delete-report`,
  RELEASE_REPORT: `${API_URL}/release-report`,
};

class BorrowingBaseService {
  getBillingLogs(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.GET_BILLING_LOGS, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  getDashboard(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.GET_DASHBOARD, { headers: authHeader(), signal });
  }

  getDocusignDocs(CompId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.GET_DOCUSIGN_DOCS, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  getLoanPlanSummary(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.GET_LOAN_PLAN_SUMMARY, {
      headers: authHeader(),
      signal,
    });
  }

  getCompaniesWithBorrowingBase(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.COMPANIES_WITH_BORROWING_BASE, {
      headers: authHeader(),
      signal,
    });
  }

  getReports(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.GET_REPORTS, { headers: authHeader(), signal });
  }

  getReportsPaginated(
    page?: number,
    size?: number,
    sortField?: string,
    sortOrder?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(routes.GET_REPORTS_PAGINATED, {
      headers: authHeader(),
      params: { page, size, sortField, sortOrder },
      signal,
      timeout: 60000,
    });
  }

  getTransactions(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.GET_TRANSACTIONS, {
      headers: authHeader(),
      signal,
    });
  }

  getTransactionsByCompId(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.GET_TRANSACTIONS_BY_COMP_ID, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  getBillingBalances(CompId: string, StartDate: Date, EndDate: Date, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.GET_BILLING_BALANCES, {
      headers: authHeader(),
      params: { CompId, StartDate, EndDate },
      signal,
    });
  }

  getBillingTransactionsDetailGrid(
    CompId: string,
    StartDate: Date,
    EndDate: Date,
    Approved: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(routes.TRANSACTIONS_DETAIL_GRID, {
      headers: authHeader(),
      params: { CompId, StartDate, EndDate, Approved },
      signal,
    });
  }

  billTransactions(CompId: string, StartDate: Date, EndDate: Date, Approved: string) {
    return authenticatedAxiosInstance.post(
      routes.BILL_TRANSACTIONS,
      {},
      { headers: authHeader(), params: { CompId, StartDate, EndDate, Approved } }
    );
  }

  /** Home > Borrowing Base Reports endpoints */
  // Create reports
  // Send To DocuSign,
  // Release
  // Delete
  async createReports(dataDate: string): Promise<CreateReportsResponse> {
    try {
      const response = await authenticatedAxiosInstance.post(
        routes.CREATE_REPORTS,
        { dataDate },
        { headers: authHeader() }
      );
      console.log('Borrowing base reports created successfully:', {
        dataDate: dataDate,
        success: response.data.success,
        reportCount: Array.isArray(response.data.data) ? response.data.data.length : 'N/A',
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as CreateReportsResponse;
      }

      return {
        success: false,
        message: 'Failed to create reports',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async deleteReport(recId: number) {
    return authenticatedAxiosInstance.post(
      routes.DELETE_REPORT,
      { recId },
      { headers: authHeader() }
    );
  }

  async releaseReport(recId: number) {
    return authenticatedAxiosInstance.post(
      routes.RELEASE_REPORT,
      { recId },
      { headers: authHeader() }
    );
  }

  /**
   * Get API endpoint documentation for debugging purposes
   * @returns {Promise} - API response with endpoint documentation
   */
  getApiDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/borrowing-base/docs`, {
      headers: authHeader(),
      signal,
    });
  }

  /**
   * Get SQL procedure documentation for debugging purposes
   * @returns {Promise} - API response with SQL procedure documentation
   */
  getSqlDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${process.env.REACT_APP_API_URL}/borrowing-base/sql-docs`,
      { headers: authHeader(), signal }
    );
  }
}

export default new BorrowingBaseService();
